import { type ReactNode } from 'react';
import { Modal as MantineModal } from '@mantine/core';
import type { ModalProps as MantineModalProps } from '@mantine/core';
import './Modal.css';

export interface ModalProps extends Omit<MantineModalProps, 'opened' | 'onClose' | 'title'> {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  maxWidth?: string | number;
}

export default function Modal({ isOpen, onClose, title, children, maxWidth, styles, classNames, ...props }: ModalProps) {
  const maxWidthValue = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

  const mergedStyles = typeof styles === 'function'
    ? styles
    : {
      ...(maxWidth && {
        content: {
          maxWidth: maxWidthValue,
          width: '100%',
        },
      }),
      ...(typeof styles === 'object' ? styles : {}),
    };

  const mergedClassNames =
    typeof classNames === 'function'
      ? classNames
      : {
          content: ['app-modal-content', typeof classNames?.content === 'string' ? classNames.content : '']
            .filter(Boolean)
            .join(' '),
          header: ['app-modal-header', typeof classNames?.header === 'string' ? classNames.header : '']
            .filter(Boolean)
            .join(' '),
          title: ['app-modal-title', typeof classNames?.title === 'string' ? classNames.title : '']
            .filter(Boolean)
            .join(' '),
          body: ['app-modal-body', typeof classNames?.body === 'string' ? classNames.body : '']
            .filter(Boolean)
            .join(' '),
          close: ['app-modal-close', typeof classNames?.close === 'string' ? classNames.close : '']
            .filter(Boolean)
            .join(' '),
        };

  return (
    <MantineModal
      opened={isOpen}
      onClose={onClose}
      size={maxWidth ? 'auto' : '600'}
      title={title}
      centered
      radius={14}
      padding={0}
      yOffset={12}
      xOffset={12}
      zIndex={1000}
      styles={mergedStyles}
      classNames={mergedClassNames}
      overlayProps={{ backgroundOpacity: 0.35, blur: 1 }}
      {...props}
    >
      {children}
    </MantineModal>
  );
}
